import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { parseDateString } from '@common/utils/date';
import { HealthDirection, MissionCodeType } from 'models/enums/fleet-management';
import { FleetDashboardFilters } from 'models/fleet-management/FleetDataFilter';
import {
  FleetDataHealth,
  FleetDataHealthTransition,
  HealthChangesAnswer,
} from 'models/fleet-management/FleetDataSet';
import { toInsightsQuery } from 'store/fleet-management/fleet-management-actions';
import { HEALTH_STATUS } from 'models/fleet-management/FleetWidget';
import CustomTable from 'components/misc/CustomTable';
import SwitchInput from 'components/forms/SwitchInput';
import { useFleetInsights } from 'hooks/useFleetDataset';
import useCodeLabel from 'hooks/useCodeLabel';
import { LineChart } from '@mui/x-charts/LineChart';
import {
  CODE_FONT,
  CODE_TONES,
  FLEET_SECTION_GAP,
  HEALTH_MARK_COLORS,
  HEALTH_STATUS_LABEL_KEYS,
  HEALTH_STATUS_ORDER,
  HEALTH_TONES,
  WEEKLY_CHART_MARGIN,
  chartSx,
  weeklyTickLabelStyle,
} from 'utils/fleet-dashboard-theme';
import openRobotPage from 'utils/open-robot-page';
import {
  RobotHealthStatus,
  TransitionSort,
  atRiskFromDays,
  atRiskRobots,
  bandsWithCounts,
  countTransitions,
  daysBetween,
  isChange,
  sortTransitions,
  transitionKey,
  versionHealthCrossTab,
} from 'utils/health-transitions';
import DashboardSection from '../DashboardSection';
import CodeChips, { CodeTooltipContent } from './CodeChips';
import DatasetState from './DatasetState';
import {
  CardGridSkeleton,
  MeterListSkeleton,
  STATUS_CARD_HEIGHT,
  TableSkeleton,
} from './SectionSkeletons';
import MeterTrack from './MeterTrack';
import StatusCardWidget from './StatusCardWidget';
import useShowDealerColumn from 'hooks/useShowDealerColumn';

const AT_RISK_LIMIT = 10;
const VERSION_LIMIT = 5;
const CODE_LIMIT = 10;
const HISTORY_CHART_HEIGHT = 300;
const BAND_SKELETON_HEIGHT = 180;

export const StatusDot: React.FC<{ status: RobotHealthStatus; size?: number }> = ({
  status,
  size = 10,
}) => (
  <Box
    component="span"
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      flexShrink: 0,
      backgroundColor: HEALTH_MARK_COLORS[status],
    }}
  />
);

/** The four statuses named once for a whole card, so no row has to repeat them. */
const StatusLegend: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Stack direction="row" gap={1.5} flexWrap="wrap">
      {HEALTH_STATUS_ORDER.map((status) => (
        <Stack key={status} direction="row" alignItems="center" gap={0.75}>
          <StatusDot status={status} />
          <Typography variant="caption" color="text.secondary">
            {t(HEALTH_STATUS_LABEL_KEYS[status])}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

const formatDay = (value: string | null, language: string) =>
  value ? DateTime.fromISO(value).setLocale(language).toLocaleString(DateTime.DATE_MED) : '—';

/** The arrow between a transition's two colours. */
const TransitionArrow: React.FC<{ width?: number }> = ({ width = 16 }) => (
  <Box
    component="svg"
    width={width}
    height={(width * 10) / 16}
    viewBox="0 0 16 10"
    fill="none"
    aria-hidden
    sx={{ flexShrink: 0, color: 'text.disabled' }}
  >
    <path
      d="M0 5 H12 M12 5 L8.5 1.5 M12 5 L8.5 8.5"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
);

/** The error and maintenance code columns: a heading, then name-over-meter rows. */
const CodeGroupsSkeleton: React.FC = () => (
  <Grid container spacing={3}>
    {[MissionCodeType.ERROR, MissionCodeType.MAINTENANCE].map((type) => (
      <Grid item xs={12} md={6} key={type}>
        <Stack gap={1.5}>
          <Skeleton variant="text" width={140} height={22} />
          {Array.from({ length: 5 }, (_, index) => (
            <Stack key={index} gap={0.5}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="rounded" height={10} />
            </Stack>
          ))}
        </Stack>
      </Grid>
    ))}
  </Grid>
);

/** Today's split of the fleet over the four statuses, read from the newest week. */
const HealthStatusSection: React.FC<{ healthData: FleetDataHealth[] | null }> = ({
  healthData,
}) => {
  const { t } = useTranslation();

  const cards = useMemo(() => {
    const latestWeek = [...(healthData ?? [])]
      .sort((a, b) => new Date(a.week_start).getTime() - new Date(b.week_start).getTime())
      .at(-1);
    const total = latestWeek?.active_robot_count ?? 0;
    const counts = latestWeek?.status_counts;

    return HEALTH_STATUS_ORDER.map((status) => {
      const count = counts?.[status] ?? 0;
      return { status, count, percentage: total ? Math.round((count / total) * 100) : 0 };
    });
  }, [healthData]);

  const info = (
    <Stack gap={1.25}>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
        {t('fleet-management-info-health-status-intro')}
      </Typography>
      <Typography variant="body2">
        {t('fleet-management-info-health-status-windows-note')}
      </Typography>
      <Stack component="ul" gap={0.5} sx={{ m: 0, pl: 0, listStyle: 'none' }}>
        {(
          [
            [HEALTH_STATUS.GREEN, 'fleet-management-info-health-status-healthy'],
            [HEALTH_STATUS.ORANGE, 'fleet-management-info-health-status-at-risk'],
            [HEALTH_STATUS.RED, 'fleet-management-info-health-status-critical'],
            [HEALTH_STATUS.GREY, 'fleet-management-info-health-status-inactive'],
          ] as const
        ).map(([status, key]) => (
          <Stack component="li" key={status} direction="row" alignItems="center" gap={0.75}>
            <StatusDot status={status} size={8} />
            <Typography variant="body2">{t(key)}</Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );

  return (
    <DashboardSection title={t('fleet-management-title-health-status')} info={info}>
      {healthData == null ? (
        <Box aria-busy="true">
          <CardGridSkeleton
            count={HEALTH_STATUS_ORDER.length}
            height={STATUS_CARD_HEIGHT}
            item={{ xs: 12, sm: 6, md: 3 }}
          />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {cards.map(({ status, count, percentage }) => (
            <Grid item xs={12} sm={6} md={3} key={status}>
              <StatusCardWidget
                tone={HEALTH_TONES[status]}
                value={count}
                percent={percentage !== 0 ? percentage : undefined}
                label={t(HEALTH_STATUS_LABEL_KEYS[status])}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </DashboardSection>
  );
};

/**
 * Each week's fleet split over the four statuses, stacked so the bands always fill to 100 %: a
 * band growing is that status taking a larger share of the robots.
 */
const HealthHistorySection: React.FC<{ healthData: FleetDataHealth[] | null }> = ({
  healthData,
}) => {
  const { t, i18n } = useTranslation();

  const weeks = useMemo(
    () =>
      [...(healthData ?? [])]
        .sort((a, b) => new Date(a.week_start).getTime() - new Date(b.week_start).getTime())
        .map((week) => {
          const counts = week.status_counts;
          const total = HEALTH_STATUS_ORDER.reduce(
            (sum, status) => sum + (counts?.[status] ?? 0),
            0,
          );
          const share = (status: RobotHealthStatus) =>
            total ? ((counts?.[status] ?? 0) / total) * 100 : 0;
          return {
            weekLabel: DateTime.fromISO(week.week_start)
              .setLocale(i18n.language)
              .toLocaleString(DateTime.DATE_MED),
            ...Object.fromEntries(HEALTH_STATUS_ORDER.map((status) => [status, share(status)])),
          };
        }),
    [healthData, i18n.language],
  );

  const percent = (value: number | null) => `${Math.round(value ?? 0)}%`;

  return (
    <DashboardSection
      title={t('fleet-management-title-health-history')}
      caption={t('fleet-management-subtitle-health-share-over-time')}
      actions={<StatusLegend />}
    >
      {healthData == null && <Skeleton variant="rounded" height={HISTORY_CHART_HEIGHT} />}
      {healthData != null && weeks.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {t('fleet-management-no-data')}
        </Typography>
      )}
      {weeks.length > 0 && (
        <LineChart
          dataset={weeks}
          xAxis={[
            { dataKey: 'weekLabel', scaleType: 'point', tickLabelStyle: weeklyTickLabelStyle },
          ]}
          yAxis={[{ min: 0, max: 100, valueFormatter: percent }]}
          series={HEALTH_STATUS_ORDER.map((status) => ({
            dataKey: status,
            label: t(HEALTH_STATUS_LABEL_KEYS[status]),
            color: HEALTH_MARK_COLORS[status],
            stack: 'health',
            area: true,
            showMark: false,
            valueFormatter: percent,
          }))}
          height={HISTORY_CHART_HEIGHT}
          margin={WEEKLY_CHART_MARGIN}
          grid={{ horizontal: true }}
          slotProps={{ legend: { hidden: true } }}
          sx={{ ...chartSx, '.MuiAreaElement-root': { fillOpacity: 0.85 } }}
        />
      )}
    </DashboardSection>
  );
};

/**
 * One from-to pair inside a band: both colours with the arrow between them, the count at the
 * right. It filters the table below, so a selected card takes its band's accent.
 */
const TransitionCard: React.FC<{
  from: RobotHealthStatus;
  to: RobotHealthStatus;
  accent: RobotHealthStatus;
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
}> = ({ from, to, accent, label, count, selected, onClick }) => {
  const tone = HEALTH_TONES[accent];
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={label}
      aria-pressed={selected}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 1.25,
        width: '100%',
        textAlign: 'left',
        borderRadius: 2,
        px: 1.5,
        py: 1.25,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        '&:hover': { borderColor: tone.mark },
        ...(selected && {
          outline: `1px solid ${tone.mark}`,
          borderColor: tone.mark,
          backgroundColor: tone.tint,
        }),
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.875} minWidth={0}>
        <StatusDot status={from} size={12} />
        <TransitionArrow />
        <StatusDot status={to} size={12} />
        <Typography variant="body2" color="text.primary" fontWeight={selected ? 700 : 400} noWrap>
          {label}
        </Typography>
      </Stack>
      <Typography
        fontSize={18}
        fontWeight={700}
        ml="auto"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {count}
      </Typography>
    </ButtonBase>
  );
};

const TransitionPill: React.FC<{ row: FleetDataHealthTransition }> = ({ row }) => {
  const { t } = useTranslation();
  const changed = isChange(row);
  const tone = HEALTH_TONES[row.new_status];
  const to = t(HEALTH_STATUS_LABEL_KEYS[row.new_status]);

  let label = t('fleet-management-transition-unchanged', { status: to });
  if (changed && row.previous_status) {
    label = t('fleet-management-transition-label', {
      from: t(HEALTH_STATUS_LABEL_KEYS[row.previous_status]),
      to,
    });
  } else if (row.direction === HealthDirection.JOINED) {
    label = t('fleet-management-transition-joined', { status: to });
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.875}
      sx={{
        px: 1.25,
        py: 0.625,
        borderRadius: 4,
        backgroundColor: changed ? tone.tint : 'common.white',
        border: '1px solid',
        borderColor: changed ? alpha(tone.mark, 0.33) : 'divider',
        maxWidth: '100%',
      }}
    >
      {changed && row.previous_status && (
        <>
          <StatusDot status={row.previous_status} />
          <TransitionArrow width={15} />
        </>
      )}
      <StatusDot status={row.new_status} />
      <Typography variant="caption" color="text.primary" noWrap>
        {label}
      </Typography>
    </Stack>
  );
};

const FleetHealthWidgets: React.FC<{
  filters: FleetDashboardFilters;
  /** The header's weekly health rows; null until they first arrive. */
  healthData: FleetDataHealth[] | null;
}> = ({ filters, healthData }) => {
  const { end } = filters;
  const { t, i18n } = useTranslation();
  const codeLabel = useCodeLabel();

  const [selectedTransition, setSelectedTransition] = useState<string | null>(null);
  const [includeUnchanged, setIncludeUnchanged] = useState(false);
  const [sort, setSort] = useState<TransitionSort>(TransitionSort.RECENT);

  const query = useMemo(() => toInsightsQuery(filters), [filters]);
  const answer = useFleetInsights<HealthChangesAnswer>('health-changes', query);
  const transitions = { ...answer, data: answer.data?.transitions ?? null };
  const codes = { ...answer, data: answer.data?.codes ?? null };

  const rows = useMemo(() => transitions.data ?? [], [transitions.data]);
  const bands = useMemo(() => bandsWithCounts(countTransitions(rows)), [rows]);
  const unchangedCount = useMemo(() => rows.filter((row) => !isChange(row)).length, [rows]);

  // A pair the new data no longer holds has no card left to click, so drop it rather than
  // leaving the table filtered to nothing.
  useEffect(() => {
    if (
      selectedTransition &&
      !bands.some((band) => band.cards.some((card) => card.key === selectedTransition))
    ) {
      setSelectedTransition(null);
    }
  }, [bands, selectedTransition]);

  // Days are counted to the end of the window, or to today for a window still running.
  const windowEnd = useMemo(
    () => DateTime.min(parseDateString(end).endOf('day'), DateTime.now()),
    [end],
  );

  const tableRows = useMemo(() => {
    let visible = rows;
    if (selectedTransition) {
      visible = rows.filter(
        (row) =>
          isChange(row) &&
          transitionKey(row.previous_status, row.new_status) === selectedTransition,
      );
    } else if (!includeUnchanged) {
      visible = rows.filter(isChange);
    }
    return sortTransitions(visible, sort);
  }, [includeUnchanged, rows, selectedTransition, sort]);

  const atRisk = useMemo(() => atRiskRobots(rows), [rows]);
  const versions = useMemo(
    () =>
      versionHealthCrossTab(
        rows,
        VERSION_LIMIT,
        t('fleet-management-label-other'),
        t('unknown-value'),
      ),
    [rows, t],
  );

  const codeGroups = useMemo(
    () =>
      [MissionCodeType.ERROR, MissionCodeType.MAINTENANCE].map((type) => ({
        type,
        codes: (codes.data ?? []).filter((code) => code.code_type === type).slice(0, CODE_LIMIT),
      })),
    [codes.data],
  );

  const statusName = (status: RobotHealthStatus) => t(HEALTH_STATUS_LABEL_KEYS[status]);
  const pairLabel = (from: RobotHealthStatus, to: RobotHealthStatus) =>
    t('fleet-management-transition-label', { from: statusName(from), to: statusName(to) });

  const selectedPair = bands
    .flatMap((band) => band.cards)
    .find((card) => card.key === selectedTransition);

  const showDealerColumn = useShowDealerColumn(filters);
  const allColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('fleet-management-field-robot'),
      width: 170,
      renderCell: (params: GridRenderCellParams) => (
        <Stack justifyContent="center" height="100%" minWidth={0}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {params.row.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {params.row.serial_number}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'customer',
      headerName: t('fleet-management-field-customer'),
      width: 180,
      valueGetter: (value) => value ?? '—',
    },
    {
      field: 'dealer',
      headerName: t('fleet-management-field-dealer'),
      width: 170,
      valueGetter: (value) => value ?? '—',
    },
    {
      field: 'direction',
      headerName: t('fleet-management-column-status-change'),
      width: 190,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Stack justifyContent="center" height="100%">
          <TransitionPill row={params.row} />
        </Stack>
      ),
    },
    {
      field: 'changed_at',
      headerName: t('fleet-management-column-changed-on'),
      width: 140,
      renderCell: (params: GridRenderCellParams) =>
        params.row.changed_at && isChange(params.row) ? (
          <Stack justifyContent="center" height="100%">
            <Typography variant="body2">
              {formatDay(params.row.changed_at, i18n.language)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('fleet-management-days-ago', {
                count: daysBetween(params.row.changed_at, windowEnd),
              })}
            </Typography>
          </Stack>
        ) : (
          '—'
        ),
    },
    {
      field: 'end_error_codes',
      headerName: t('fleet-management-column-errors-at-end'),
      description: t('fleet-management-info-codes-at-end'),
      width: 170,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <CodeChips
          codes={params.row.end_error_codes}
          windowCodes={params.row.window_error_codes}
          type={MissionCodeType.ERROR}
        />
      ),
    },
    {
      field: 'end_maintenance_codes',
      headerName: t('fleet-management-column-maintenance-at-end'),
      description: t('fleet-management-info-codes-at-end'),
      width: 170,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <CodeChips
          codes={params.row.end_maintenance_codes}
          windowCodes={params.row.window_maintenance_codes}
          type={MissionCodeType.MAINTENANCE}
        />
      ),
    },
    {
      field: 'last_successful_mission_date',
      headerName: t('fleet-management-field-last_successful_mission_date'),
      width: 150,
      valueFormatter: (value: string | null) => formatDay(value, i18n.language),
    },
    {
      field: 'open',
      headerName: '',
      width: 100,
      sortable: false,
      disableReorder: true,
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" width="100%" justifyContent="end" height="100%">
          <Button
            fullWidth
            onClick={(event) => {
              event.stopPropagation();
              openRobotPage(params.row.id);
            }}
          >
            {t('button-open')}
          </Button>
        </Stack>
      ),
    },
  ];
  const columns = showDealerColumn
    ? allColumns
    : allColumns.filter((column) => column.field !== 'dealer');

  const tableActions = (
    <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
      <SwitchInput
        size="small"
        label={t('fleet-management-include-unchanged', { count: unchangedCount })}
        isChecked={includeUnchanged}
        disabled={selectedTransition != null}
        onChange={() => setIncludeUnchanged((value) => !value)}
      />
      <ToggleButtonGroup
        size="small"
        exclusive
        color="primary"
        value={sort}
        aria-label={t('fleet-management-sort-changes')}
        onChange={(_, value: TransitionSort | null) => value && setSort(value)}
      >
        <ToggleButton value={TransitionSort.RECENT} sx={{ px: 2 }}>
          {t('fleet-management-sort-most-recent')}
        </ToggleButton>
        <ToggleButton value={TransitionSort.SEVERITY} sx={{ px: 2 }}>
          {t('fleet-management-sort-severity')}
        </ToggleButton>
      </ToggleButtonGroup>
    </Stack>
  );

  const transitionState = {
    isLoading: transitions.isLoading,
    hasError: transitions.hasError,
    hasData: transitions.data != null,
  };

  return (
    <Stack gap={FLEET_SECTION_GAP} width="100%">
      <HealthStatusSection healthData={healthData} />

      <HealthHistorySection healthData={healthData} />

      <DashboardSection
        title={t('fleet-management-title-status-transitions')}
        caption={t('fleet-management-caption-status-transitions')}
        actions={
          selectedTransition && (
            <Button size="small" onClick={() => setSelectedTransition(null)}>
              {t('fleet-management-clear-selection')}
            </Button>
          )
        }
      >
        <DatasetState
          {...transitionState}
          skeleton={
            <CardGridSkeleton count={3} height={BAND_SKELETON_HEIGHT} item={{ xs: 12, md: 4 }} />
          }
        >
          <Grid container spacing={2}>
            {bands.map((band) => {
              const accent = HEALTH_TONES[band.accent];
              return (
                <Grid item xs={12} md={4} key={band.direction}>
                  <Stack
                    gap={1.25}
                    height="100%"
                    role="group"
                    aria-label={t(band.titleKey)}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderTop: `3px solid ${accent.mark}`,
                      borderRadius: 2,
                      px: 2,
                      py: 1.75,
                    }}
                  >
                    <Stack direction="row" alignItems="baseline" gap={1}>
                      <Typography
                        variant="overline"
                        fontWeight={700}
                        lineHeight={1.4}
                        color={accent.ink}
                      >
                        {t(band.titleKey)}
                      </Typography>
                      <Typography
                        fontSize={20}
                        fontWeight={700}
                        lineHeight={1}
                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {band.total}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('fleet-management-robots-unit')}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {t(band.noteKey)}
                    </Typography>
                    {band.cards.length === 0 && (
                      <Typography variant="body2" color="text.disabled">
                        {t('fleet-management-band-empty')}
                      </Typography>
                    )}
                    <Stack gap={1}>
                      {band.cards.map((card) => (
                        <TransitionCard
                          key={card.key}
                          from={card.from}
                          to={card.to}
                          accent={band.accent}
                          label={pairLabel(card.from, card.to)}
                          count={card.count}
                          selected={selectedTransition === card.key}
                          onClick={() =>
                            setSelectedTransition((current) =>
                              current === card.key ? null : card.key,
                            )
                          }
                        />
                      ))}
                    </Stack>
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
        </DatasetState>
      </DashboardSection>

      <DashboardSection
        title={
          selectedPair
            ? t('fleet-management-title-changes-filtered', {
                transition: pairLabel(selectedPair.from, selectedPair.to),
              })
            : t('fleet-management-title-changes')
        }
        caption={t('fleet-management-robot-count', { count: tableRows.length })}
        actions={tableActions}
      >
        <DatasetState {...transitionState} skeleton={<TableSkeleton />}>
          <Box sx={{ '& .transition-unchanged': { opacity: 0.62 } }}>
            <CustomTable
              getStatus={HttpFetchStatus.Success}
              dataLoaded
              failedToLoadMessage={t('fleet-management-error')}
              emptyDataMessage={t('fleet-management-no-health-changes')}
              data={tableRows}
              columns={columns}
              getRowClassName={(params) =>
                isChange(params.row as FleetDataHealthTransition) ? '' : 'transition-unchanged'
              }
              autoHeight
            />
          </Box>
        </DatasetState>
      </DashboardSection>

      <Stack direction={{ xs: 'column', lg: 'row' }} gap={FLEET_SECTION_GAP} alignItems="stretch">
        <DashboardSection
          title={t('fleet-management-title-at-risk')}
          caption={t('fleet-management-caption-at-risk')}
          info={t('fleet-management-info-at-risk')}
          sx={{ flex: '1 1 0' }}
        >
          <DatasetState {...transitionState} skeleton={<MeterListSkeleton />}>
            <Stack gap={1.25}>
              {atRisk.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('fleet-management-at-risk-empty')}
                </Typography>
              )}
              {atRisk.slice(0, AT_RISK_LIMIT).map((row) => {
                const days = row.days_since_success ?? 0;
                const limit = Math.max(1, row.green_limit_days);
                return (
                  <Stack key={row.id} direction="row" alignItems="center" gap={1.5}>
                    <Typography variant="body2" noWrap sx={{ flex: '1 1 40%', minWidth: 0 }}>
                      {row.customer ? `${row.name} · ${row.customer}` : row.name}
                    </Typography>
                    <Box sx={{ flex: '1 1 45%', minWidth: 72, maxWidth: 240 }}>
                      <MeterTrack>
                        <Box
                          sx={{
                            flex: `0 0 ${Math.min(100, (days / limit) * 100)}%`,
                            backgroundColor: HEALTH_MARK_COLORS[HEALTH_STATUS.ORANGE],
                          }}
                        />
                        {/* Where at risk begins, so the fill reads against the threshold. */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            left: `${(atRiskFromDays(limit) / limit) * 100}%`,
                            width: '2px',
                            backgroundColor: 'text.secondary',
                          }}
                        />
                      </MeterTrack>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      width={96}
                      textAlign="right"
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {t('fleet-management-at-risk-days', { days, limit })}
                    </Typography>
                  </Stack>
                );
              })}
              {atRisk.length > AT_RISK_LIMIT && (
                <Typography variant="caption" color="text.secondary">
                  {t('fleet-management-and-more', { count: atRisk.length - AT_RISK_LIMIT })}
                </Typography>
              )}
            </Stack>
          </DatasetState>
        </DashboardSection>

        <DashboardSection
          title={t('fleet-management-title-version-health')}
          caption={t('fleet-management-caption-version-health')}
          actions={<StatusLegend />}
          sx={{ flex: '1 1 0' }}
        >
          <DatasetState {...transitionState} skeleton={<MeterListSkeleton rows={4} />}>
            <Stack gap={1.25}>
              {versions.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('fleet-management-no-data')}
                </Typography>
              )}
              {versions.map((version) => (
                <Tooltip
                  key={version.version}
                  placement="top"
                  followCursor
                  title={HEALTH_STATUS_ORDER.map(
                    (status) => `${statusName(status)} ${version.counts[status]}`,
                  ).join(' · ')}
                >
                  <Stack direction="row" alignItems="center" gap={1.5}>
                    <Typography variant="body2" noWrap width={96} fontFamily={CODE_FONT}>
                      {version.version}
                    </Typography>
                    <MeterTrack sx={{ flex: 1 }}>
                      {HEALTH_STATUS_ORDER.filter((status) => version.counts[status] > 0).map(
                        (status) => (
                          <Box
                            key={status}
                            sx={{
                              flex: `0 0 ${(version.counts[status] / version.total) * 100}%`,
                              backgroundColor: HEALTH_MARK_COLORS[status],
                            }}
                          />
                        ),
                      )}
                    </MeterTrack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      width={40}
                      textAlign="right"
                      sx={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {version.total}
                    </Typography>
                  </Stack>
                </Tooltip>
              ))}
            </Stack>
          </DatasetState>
        </DashboardSection>
      </Stack>

      <DashboardSection
        title={t('fleet-management-title-top-codes')}
        caption={t('fleet-management-caption-top-codes')}
      >
        <DatasetState
          isLoading={codes.isLoading}
          hasError={codes.hasError}
          hasData={codes.data != null}
          skeleton={<CodeGroupsSkeleton />}
        >
          <Grid container spacing={3}>
            {codeGroups.map((group) => {
              const tone = CODE_TONES[group.type];
              const maxRobots = Math.max(1, ...group.codes.map((code) => code.robot_count));
              return (
                <Grid item xs={12} md={6} key={group.type}>
                  <Stack gap={1.5}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t(
                        group.type === MissionCodeType.ERROR
                          ? 'fleet-management-title-error-codes'
                          : 'fleet-management-title-maintenance-codes',
                      )}
                    </Typography>
                    {group.codes.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        {t('fleet-management-no-codes')}
                      </Typography>
                    )}
                    {group.codes.map((code) => (
                      <Tooltip
                        key={code.code}
                        placement="top"
                        title={
                          <Stack gap={0.5}>
                            <CodeTooltipContent code={code.code} type={group.type} />
                            <Typography variant="caption">
                              {t('fleet-management-code-sources', {
                                end: code.end_occurrences,
                                run: code.run_occurrences,
                              })}
                            </Typography>
                          </Stack>
                        }
                      >
                        <Stack gap={0.5}>
                          <Stack direction="row" gap={1} alignItems="baseline" minWidth={0}>
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              noWrap
                              flex={1}
                              minWidth={0}
                              color={tone.ink}
                            >
                              {codeLabel(code.code, group.type).title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                              sx={{ fontVariantNumeric: 'tabular-nums' }}
                            >
                              {t('fleet-management-code-figures', {
                                robots: code.robot_count,
                                missions: code.occurrences,
                              })}
                            </Typography>
                          </Stack>
                          <MeterTrack>
                            <Box
                              sx={{
                                flex: `0 0 ${(code.robot_count / maxRobots) * 100}%`,
                                borderRadius: 1,
                                backgroundColor: tone.mark,
                              }}
                            />
                          </MeterTrack>
                        </Stack>
                      </Tooltip>
                    ))}
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
        </DatasetState>
      </DashboardSection>
    </Stack>
  );
};

export default React.memo(FleetHealthWidgets);
